import React, { useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import VariantSelection from '../VariantSelection';
import messages from './messages';
import styles from './ProductVariantSelection.module.scss';
import QuantityCounter from '../../../components/common/QuantityCounter';
import QuantityButtons from '../../../components/common/QuantityButtons';
import { ProductVariantSelectionProps } from './types';
import { PRODUCT_TYPES_SINGULAR } from '../../collectionPDP/ProductSelection';
import { SIZE_ATTRIBUTES } from '../constants';

const ProductVariantSelection = ({
  onFirstProductQuantityIncrement,
  onFirstProductQuantityDecrement,
  onSelectFirstSKU,
  onSelectSecondSKU,
  product,
  selectedFirstSKU,
  selectedFirstSKUQuantity,
  selectedSecondSKU,
  selectedFirstSKUDiscount,
}: ProductVariantSelectionProps) => {
  const { formatMessage } = useIntl();

  const isMattress = product.productTypeSingular === 'mattress';

  const productTypesWithQuantityCounter = [
    PRODUCT_TYPES_SINGULAR.mattress,
    PRODUCT_TYPES_SINGULAR.bedFrames,
  ];
  const showQuantityCounter = !productTypesWithQuantityCounter.includes(
    product?.productTypeSingular,
  );

  const hasSecondSkuPromo = product?.secondItemDiscount?.percentage;

  const { secondaryVariants } = useMemo(() => {
    // filter out variants to get only the variants of the matching size attribute of the first sku
    const matchingSizeAttribute =
      product.variants
        .find((variant) => variant.sku === selectedFirstSKU)
        ?.attributes.find((attr) => SIZE_ATTRIBUTES.includes(attr.name || ''))
        ?.key ?? '';

    const filteredVariants =
      product.variants.filter((variant) =>
        variant.attributes.some((attr) => attr.key === matchingSizeAttribute),
      ) ?? product.variants;

    return {
      secondaryVariants: filteredVariants,
    };
  }, [product.variants, selectedFirstSKU]);

  useEffect(() => {
    if (selectedSecondSKU) {
      const isSelectedSecondSKUValid = secondaryVariants.some(
        (variant) => variant.sku === selectedSecondSKU,
      );
      if (!isSelectedSecondSKUValid) {
        const selectedSecondSKUAttributes =
          product.variants
            .find((variant) => variant.sku === selectedSecondSKU)
            ?.attributes.filter(
              (attr) => !SIZE_ATTRIBUTES.includes(attr.name || ''),
            ) ?? [];

        const matchingVariant = secondaryVariants.find((variant) => {
          const variantAttributes = variant.attributes.filter(
            (attr) => !SIZE_ATTRIBUTES.includes(attr.name || ''),
          );
          return (
            JSON.stringify(variantAttributes) ===
            JSON.stringify(selectedSecondSKUAttributes)
          );
        });

        if (matchingVariant) {
          onSelectSecondSKU(matchingVariant.sku);
        } else {
          onSelectSecondSKU(secondaryVariants[0].sku);
        }
      }
    }
  }, [
    secondaryVariants,
    selectedSecondSKU,
    onSelectSecondSKU,
    product.variants,
  ]);

  return (
    <>
      <div className={styles['variant-selection-container']}>
        <VariantSelection
          variants={product.variants}
          productKey={product.key}
          productName={product.name}
          attributesExtraInfo={product?.attributesExtraInfo}
          attributesUpperTitle={
            selectedSecondSKU &&
            formatMessage(messages.firstProductAttributesUpperTitle, {
              productName: product.productTypeSingular,
            })
          }
          isShowingDiscountedPrices
          defaultSelectedSKU={selectedFirstSKU}
          onSelectVariant={({ sku }: { sku: string }) => {
            onSelectFirstSKU(sku);
          }}
          discountSavings={isMattress ? selectedFirstSKUDiscount : undefined}
        />
      </div>
      {!hasSecondSkuPromo && showQuantityCounter && (
        <div
          className={classnames(styles['quantity-container'], [
            styles['quantitiy-container-margin'],
          ])}
        >
          <div className={styles['quantity-text']}>
            {formatMessage(messages.selectQuantity)}
          </div>
          <QuantityCounter
            theme="pdp"
            quantity={selectedFirstSKUQuantity}
            isDecrementButtonDisabled={selectedFirstSKUQuantity === 1}
            onQuantityIncrement={onFirstProductQuantityIncrement}
            onQuantityDecrement={onFirstProductQuantityDecrement}
          />
        </div>
      )}
      {hasSecondSkuPromo && showQuantityCounter && (
        <QuantityButtons
          preAddedLabel={product?.secondItemDiscount?.percentage}
          onChange={() => {
            onSelectSecondSKU(selectedSecondSKU ? undefined : selectedFirstSKU);
          }}
        />
      )}
      {hasSecondSkuPromo && selectedSecondSKU && (
        <div
          className={classnames(
            styles['variant-selection-container'],
            styles['second-variant-container'],
          )}
        >
          <VariantSelection
            variants={secondaryVariants}
            productKey={product.key}
            productName={product.name}
            attributesExtraInfo={product.attributesExtraInfo}
            attributesUpperTitle={
              selectedSecondSKU &&
              formatMessage(messages.secondProductAttributesUpperTitle, {
                productName: product.productTypeSingular,
              })
            }
            isShowingDiscountedPrices
            defaultSelectedSKU={selectedSecondSKU}
            onSelectVariant={({ sku }: { sku: string }) => {
              onSelectSecondSKU(sku);
            }}
            discountSavings={isMattress ? selectedFirstSKUDiscount : undefined}
            isSecondSku
          />
        </div>
      )}
    </>
  );
};

export default ProductVariantSelection;
